import { useRef } from "react";

import { BILLING, formatNumber } from "./licensingTheme";
import { useRingSweep } from "./licensingMotion";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const SIZE = 176;
const CENTER = SIZE / 2;
const RENEW_ON = "#1f6f8b";
const RENEW_OFF = "#c9972b";

function Ring({ radius, width, segments, total, onSelect, bindTip }) {
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return segments.map((segment) => {
    const fraction = total ? segment.value / total : 0;
    const length = circumference * fraction;
    const rotation = (offset / (total || 1)) * 360 - 90;
    offset += segment.value;
    if (!length) return null;

    return (
      <circle
        key={segment.key}
        cx={CENTER}
        cy={CENTER}
        r={radius}
        fill="none"
        stroke={segment.color}
        strokeWidth={width}
        strokeDasharray={`${length} ${circumference - length}`}
        strokeDashoffset="0"
        transform={`rotate(${rotation} ${CENTER} ${CENTER})`}
        className="mcap-lic-ring-seg is-clickable"
        data-ring
        role="button"
        tabIndex={0}
        onClick={() => onSelect(segment)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(segment);
          }
        }}
        aria-label={`${segment.label}: ${formatNumber(segment.value)}`}
        {...bindTip(segment)}
      />
    );
  });
}

/**
 * The paid plans, two ways: how long each plan is (outer ring) and whether it
 * renews by itself (inner ring). The legend sits beside the rings, grouped the
 * same way, so every number is next to what it is part of.
 */
export default function PlanRings({ plans, loading, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const p = plans ?? {};
  const monthly = (p.monthlyOn ?? 0) + (p.monthlyOff ?? 0);
  const yearly = (p.yearlyOn ?? 0) + (p.yearlyOff ?? 0);
  const on = (p.monthlyOn ?? 0) + (p.yearlyOn ?? 0);
  const off = (p.monthlyOff ?? 0) + (p.yearlyOff ?? 0);
  const total = monthly + yearly;

  useRingSweep(scope, [JSON.stringify(p)]);

  const share = (value) => (total ? `${Math.round((value / total) * 100)}%` : "—");
  const groups = [
    {
      title: "Plan length",
      segments: [
        { key: "monthly", label: "Monthly", color: BILLING.paid_monthly.color, value: monthly, filters: { interval: "monthly" } },
        { key: "yearly", label: "Yearly", color: BILLING.paid_yearly.color, value: yearly, filters: { interval: "yearly" } },
      ],
    },
    {
      title: "Auto-renew",
      segments: [
        { key: "on", label: "On", color: RENEW_ON, value: on, filters: { autoRenew: "true" } },
        { key: "off", label: "Off", color: RENEW_OFF, value: off, filters: { autoRenew: "false" } },
      ],
    },
  ];

  const segmentTip = (segment) =>
    bind({
      title: segment.key === "on" ? "Auto-renew on" : segment.key === "off" ? "Auto-renew off — won't renew" : segment.label,
      rows: [
        { label: "Teachers", value: formatNumber(segment.value), color: segment.color },
        { label: "% of paid plans", value: share(segment.value) },
      ],
      footer: segment.value ? "Click to see who" : undefined,
    });

  const select = (segment) => {
    if (!segment.value) return;
    hide();
    onDrill({ type: "cohort", title: `Paid plans · ${segment.label}`, filters: { billingState: "paying", ...segment.filters } });
  };

  return (
    <section className="mcap-lic-panel" aria-labelledby="mcap-lic-plans-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-plans-title" className="mcap-lic-panel-title">
            Paid plans
          </h3>
          <p className="mcap-lic-panel-sub">
            {loading ? " " : `${formatNumber(total)} teacher${total === 1 ? "" : "s"} paying`}
          </p>
        </div>
      </header>

      <div className="mcap-lic-rings">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="mcap-lic-ring-svg" role="img" aria-label="Plan length and auto-renew">
          <circle cx={CENTER} cy={CENTER} r={74} fill="none" stroke="var(--lic-line)" strokeWidth="16" />
          <circle cx={CENTER} cy={CENTER} r={52} fill="none" stroke="var(--lic-line)" strokeWidth="12" />
          <Ring radius={74} width={16} segments={groups[0].segments} total={total} onSelect={select} bindTip={segmentTip} />
          <Ring radius={52} width={12} segments={groups[1].segments} total={total} onSelect={select} bindTip={segmentTip} />
          <text x={CENTER} y={CENTER + 4} textAnchor="middle" className="mcap-lic-ring-total">
            {loading ? "—" : formatNumber(total)}
          </text>
          <text x={CENTER} y={CENTER + 24} textAnchor="middle" className="mcap-lic-ring-caption">
            paying
          </text>
        </svg>

        <div className="mcap-lic-ring-groups">
          {groups.map((group, index) => (
            <div key={group.title} className="mcap-lic-ring-group">
              <p className="mcap-lic-ring-group-title">
                {group.title}
                <span>{index === 0 ? "outer ring" : "inner ring"}</span>
              </p>
              {group.segments.map((segment) => (
                <button
                  key={segment.key}
                  type="button"
                  className="mcap-lic-ring-item"
                  onClick={() => select(segment)}
                  disabled={!segment.value}
                  {...segmentTip(segment)}
                >
                  <span className="mcap-lic-legend-dot" style={{ background: segment.color }} aria-hidden="true" />
                  <span className="mcap-lic-legend-label">{segment.label}</span>
                  <span className="mcap-lic-legend-value">{formatNumber(segment.value)}</span>
                  <span className="mcap-lic-ring-share">{share(segment.value)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      <ChartTooltip tip={tip} />
    </section>
  );
}
